<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
    @click.self="close"
  >
    <div class="w-full max-w-5xl bg-white shadow-2xl overflow-hidden flex flex-col cf-ml-h">
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <div>
          <h3 class="m-0 text-lg font-bold text-heading">Media Library</h3>
          <p class="text-sm text-slate-500 m-0 mt-1">Upload once, reuse across pages</p>
        </div>
        <button
          type="button"
          class="text-slate-500 bg-transparent border-0 text-2xl leading-none cursor-pointer px-2"
          aria-label="Close"
          @click="close"
        >
          &times;
        </button>
      </div>

      <div class="px-6 py-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
        <input
          v-model="search"
          type="text"
          placeholder="Search by filename..."
          class="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg text-sm"
          @keyup.enter="load(1)"
        />
        <button
          type="button"
          class="px-4 py-2 text-sm font-semibold text-white bg-primary border-0 rounded-lg cursor-pointer hover:bg-primary-600"
          @click="load(1)"
        >
          Search
        </button>
        <label class="px-4 py-2 text-sm font-semibold text-primary border border-primary rounded-lg cursor-pointer hover:bg-primary hover:text-white transition-colors duration-200">
          <input ref="fileInput" type="file" class="hidden" :accept="accept" @change="onFileSelected" />
          Upload {{ mediaType === 'video' ? 'video' : 'image' }}
        </label>
      </div>

      <div class="flex-1 overflow-y-auto p-6 bg-gray-50">
        <div v-if="uploading" class="mb-4 text-sm text-six font-medium">Uploading...</div>
        <div v-if="loading" class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
          <div v-for="n in 10" :key="'sh-' + n" class="bg-gray-200 animate-pulse rounded-lg cf-ml-tile"></div>
        </div>
        <div v-else-if="!items.length" class="text-center py-16 text-slate-500">
          No media yet. Upload an image to get started.
        </div>
        <div v-else class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
          <button
            v-for="item in items"
            :key="item.id"
            type="button"
            class="text-left bg-white border rounded-lg overflow-hidden cursor-pointer hover:border-six transition-colors duration-200"
            :class="selectedId === item.id ? 'border-six' : 'border-gray-200'"
            @click="selectedId = item.id"
            @dblclick="confirmSelect(item)"
          >
            <div class="cf-ml-tile bg-gray-100 bg-cover bg-center" :style="tileStyle(item)"></div>
            <p class="px-2 py-2 text-xs text-slate-600 truncate m-0">{{ item.original_name }}</p>
          </button>
        </div>
      </div>

      <div class="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
        <p class="text-xs text-slate-500 m-0">{{ paginatorInfo && paginatorInfo.total ? paginatorInfo.total : 0 }} files</p>
        <div class="flex items-center gap-2">
          <button
            v-if="paginatorInfo && paginatorInfo.lastPage > 1"
            type="button"
            class="px-3 py-2 text-xs font-semibold bg-gray-100 border-0 rounded-lg cursor-pointer"
            :disabled="page <= 1"
            @click="load(page - 1)"
          >
            Prev
          </button>
          <button
            v-if="paginatorInfo && paginatorInfo.hasMorePages"
            type="button"
            class="px-3 py-2 text-xs font-semibold bg-gray-100 border-0 rounded-lg cursor-pointer"
            @click="load(page + 1)"
          >
            Next
          </button>
          <button
            type="button"
            class="px-4 py-2 text-sm font-semibold text-slate-600 bg-gray-100 border-0 rounded-lg cursor-pointer"
            @click="close"
          >
            Cancel
          </button>
          <button
            type="button"
            class="px-4 py-2 text-sm font-semibold text-white bg-primary border-0 rounded-lg cursor-pointer hover:bg-primary-600"
            :disabled="!selectedItem"
            @click="confirmSelect(selectedItem)"
          >
            Use selected
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import { mediaPublicUrl, cloneApolloData } from "../graphql";

const LIST_QUERY = gql`
  query MediaLibraryList($first: Int!, $page: Int!, $search: String, $media_type: String, $folder: String) {
    mediaLibraryList(first: $first, page: $page, search: $search, media_type: $media_type, folder: $folder) {
      error
      message
      paginatorInfo { currentPage lastPage total hasMorePages }
      data {
        id
        url
        original_name
        media_type
        alt_text
        folder
      }
    }
  }
`;

const UPLOAD_MUTATION = gql`
  mutation UploadMediaLibrary($file: Upload!, $folder: String, $alt_text: String) {
    uploadMediaLibrary(file: $file, folder: $folder, alt_text: $alt_text) {
      error
      message
      data {
        id
        url
        original_name
        media_type
        alt_text
        folder
      }
    }
  }
`;

export default {
  name: "MediaLibraryPicker",
  props: {
    mediaType: { type: String, default: "image" },
    folder: { type: String, default: "career-fair" },
  },
  data() {
    return {
      loading: false,
      uploading: false,
      search: "",
      items: [],
      selectedId: null,
      paginatorInfo: null,
      first: 24,
      page: 1,
    };
  },
  computed: {
    accept() {
      return this.mediaType === "video" ? "video/mp4,video/webm,video/quicktime" : "image/png,image/jpeg,image/jpg,image/webp,image/gif";
    },
    selectedItem() {
      var self = this;
      return this.items.find(function (item) {
        return item.id === self.selectedId;
      }) || null;
    },
  },
  created() {
    this.load(1);
  },
  methods: {
    tileStyle(item) {
      return { backgroundImage: "url(" + mediaPublicUrl(item) + ")" };
    },
    close() {
      this.$emit("close");
    },
    confirmSelect(item) {
      if (!item) return;
      this.$emit("select", item);
    },
    setItems(list, paginatorInfo) {
      this.items = cloneApolloData(Array.isArray(list) ? list : []);
      if (paginatorInfo) {
        this.paginatorInfo = cloneApolloData(paginatorInfo);
      }
    },
    async load(page) {
      this.page = page || 1;
      this.loading = true;
      try {
        var result = await this.$apollo.query({
          query: LIST_QUERY,
          variables: {
            first: this.first,
            page: this.page,
            search: this.search || "",
            media_type: this.mediaType || "",
            folder: "",
          },
          fetchPolicy: "network-only",
          errorPolicy: "all",
        });
        var payload = result.data && result.data.mediaLibraryList;
        if (payload && payload.error) {
          if (this.$swal) this.$swal.fire({ icon: "error", title: "Media library", text: payload.message });
        } else if (payload) {
          this.setItems(payload.data || [], payload.paginatorInfo);
        }
      } catch (e) {
        if (this.$swal) this.$swal.fire({ icon: "error", title: "Failed", text: "Could not load media library." });
      } finally {
        this.loading = false;
      }
    },
    extractUploadPayload(resultOrError) {
      if (!resultOrError) return null;
      if (resultOrError.data && resultOrError.data.uploadMediaLibrary) {
        return resultOrError.data.uploadMediaLibrary;
      }
      if (
        resultOrError.networkError &&
        resultOrError.networkError.result &&
        resultOrError.networkError.result.data &&
        resultOrError.networkError.result.data.uploadMediaLibrary
      ) {
        return resultOrError.networkError.result.data.uploadMediaLibrary;
      }
      return null;
    },
    isApolloFreezeError(error) {
      var msg = error && error.message ? String(error.message) : "";
      return msg.indexOf("not extensible") !== -1 || msg.indexOf("read only") !== -1;
    },
    async onFileSelected(event) {
      var files = event.target && event.target.files;
      var file = files && files[0];
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
      if (!file) return;
      this.uploading = true;
      var uploadedId = null;
      var payload = null;
      try {
        // Avoid Apollo writing into frozen cached list results from mediaLibraryList.
        try {
          var client = this.$apollo.getClient && this.$apollo.getClient();
          if (client && client.cache && client.cache.evict) {
            client.cache.evict({ fieldName: "mediaLibraryList" });
            if (client.cache.gc) client.cache.gc();
          }
        } catch (cacheErr) {
          // ignore cache cleanup failures
        }

        var result = await this.$apollo.mutate({
          mutation: UPLOAD_MUTATION,
          variables: {
            file: file,
            folder: this.folder,
            alt_text: null,
          },
          errorPolicy: "all",
          fetchPolicy: "no-cache",
          update: function () {},
        });
        payload = this.extractUploadPayload(result);
      } catch (e) {
        payload = this.extractUploadPayload(e);
        if (!payload && !this.isApolloFreezeError(e)) {
          if (this.$swal) {
            this.$swal.fire({
              icon: "error",
              title: "Upload failed",
              text: (e && e.message) || "Please try another file.",
            });
          }
          this.uploading = false;
          return;
        }
      }

      try {
        if (payload && payload.error) {
          if (this.$swal) this.$swal.fire({ icon: "error", title: "Upload failed", text: payload.message });
          return;
        }
        if (payload && payload.data && payload.data.id) {
          uploadedId = payload.data.id;
        }
        // Always reload from server — never mutate Apollo's frozen arrays in place.
        await this.load(1);
        if (uploadedId) {
          this.selectedId = uploadedId;
        } else if (this.items.length) {
          this.selectedId = this.items[0].id;
        }
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>

<style scoped>
.cf-ml-h {
  max-height: 90vh;
  height: 90vh;
}
.cf-ml-tile {
  height: 7.5rem;
}
</style>
